import { IconCode } from "@multiweb/types/contracts/iconCode";
import { IDisposable } from "Global/Base/IDisposable";
import { BEMClassNameCreator, setupBem } from "Global/Dom/bem";
import { EventsController } from "Global/Dom/EventsController";
import { createElement } from "UI/UI.ElementarControls/Utils/CustomElementUtils";

export class PopupMenu2 implements IDisposable {
    private _element: HTMLElement;
    private _options: PopupMenuOptions;
    private _visible: boolean = false;
    private _lastOpenedTarget: EventTarget = null;
    private _skipNextOpen: boolean = false;
    private _eventsController: EventsController = new EventsController();

    private _TOP_BAR_HEIGHT: number = 16;

    public get visible(): boolean {
        return this._visible;
    }

    private static _elementDefaultOptions: PopupMenuOptionsElement = {
        content: null,
        height: "content",
        width: "auto",
        showPosition: "auto",
        target: null,
        showAfterCreate: false,
        closeButton: false,
    };

    constructor(options: PopupMenuOptions) {
        const bemClass = setupBem("PopupMenu");
        this._element = createElement({ classes: [bemClass(), "popup-menu"] });
        this._options = options;
        if (options.closeButton)
            this._createCloseButton(bemClass);

        this._element.appendChild(this._options.content);
        document.body.appendChild(this._element);

        const clickListener = (e: MouseEvent | CustomEvent) => {
            if (!this._visible) return;
            if (e.target == this._lastOpenedTarget) return;

            const x = (e instanceof MouseEvent) ? e.clientX : e.detail.x;
            const y = (e instanceof MouseEvent) ? e.clientY : e.detail.y;

            if (!(this._element.offsetTop <= y && (this._element.offsetTop + this._element.offsetHeight) >= y &&
                this._element.offsetLeft <= x && (this._element.offsetLeft + this._element.offsetWidth) >= x)) {
                this.close();
            }
        };
        this._eventsController.addListener(window, "click", clickListener);
        this._eventsController.addListener(window, "clickCancelled", clickListener);
        this._eventsController.addListener(window, "keydown", (e: KeyboardEvent) => {
            if (e.keyCode == 27) { //Escape
                this.close();
                e.stopPropagation();
            }
        });
    }

    public show(e?: Event): void {
        if (this._skipNextOpen) {
            this._skipNextOpen = false;
            return;
        }
        if (e) {
            //e.stopPropagation();
            //EventHelper.stopPropagation(e);
            this._lastOpenedTarget = e.target;
        }

        this._setStyle("display", "block");
        this._visible = true;

        if (this._options.target instanceof HTMLElement) {
            const o = { ...PopupMenu2._elementDefaultOptions, ...this._options } as PopupMenuOptionsElement;
            const targetBoundRect = o.target.getBoundingClientRect();
            const elementTop = targetBoundRect.top;
            const elementLeft = targetBoundRect.left;
            const elementHeight = targetBoundRect.height;
            const elementWidth = targetBoundRect.width;
            const elementRight = elementLeft + elementWidth;

            if (o.width == "auto") this._setStyle("width", o.target.offsetWidth);
            else if (typeof o.width === "number") this._setStyle("width", o.width);

            if (typeof o.height === "number") this._setStyle("height", o.height);

            if (o.showPosition == "auto")
                o.showPosition = this._fitsVertically(targetBoundRect, o.showPosition) ? "after" : "before";

            if (this._fitsHorizontally(targetBoundRect)) // otevření doprava
                this._setStyle("left", elementLeft);
            else // otevření doleva
                this._setStyle("left", Math.min(document.body.clientWidth, elementRight) - this._element.offsetWidth);

            let top;
            if (o.showPosition == "after") top = elementTop + elementHeight;
            else if (o.showPosition == "overlay")
                top = this._fitsVertically(targetBoundRect, o.showPosition) ? elementTop : elementTop - this._element.offsetHeight + elementHeight;
            else top = elementTop - this._element.offsetHeight;

            if (top < 0) top = 0;

            this._setStyle("top", top);

            this._element.style["maxWidth"] = document.body.clientWidth - this._element.offsetLeft + "px";
            this._element.style["maxHeight"] = document.body.clientHeight - this._element.offsetTop + "px";

            const topBarHeight = this._options.closeButton ? this._TOP_BAR_HEIGHT : 0;
            this._options.content.style["maxWidth"] = document.body.clientWidth - this._element.offsetLeft + "px";
            this._options.content.style["maxHeight"] = document.body.clientHeight - this._element.offsetTop - topBarHeight + "px";
        }
    }

    public close(): void {
        this._setStyle("display", "none");
        this._visible = false;
        if (this._lastOpenedTarget && this._lastOpenedTarget instanceof HTMLElement) {
            if (this._options.targetShowsPopupOnFocus) {
                this._skipNextOpen = true;
            }
            // Pokud máme uložený _lastOpenedTarget, po zavření ho zaměříme.
            this._lastOpenedTarget.focus();
        }
    }

    private _setStyle(styleName: keyof CSSStyleDeclaration, value: string | number): void {
        if (typeof value === "number") value = `${value}px`;

        this._element.style[styleName as any] = value;
    }

    private _fitsHorizontally(rect: DOMRect): boolean {
        return rect.left + this._element.offsetWidth <= document.body.clientWidth;
    }

    private _fitsVertically(rect: DOMRect, showPosition: string): boolean {
        return rect.top + (showPosition === "auto" ? rect.height : 0) + this._element.offsetHeight <= document.body.clientHeight;
    }

    private _createCloseButton(bemClass: BEMClassNameCreator): void {
        const closeButton = createElement({ classes: ["btn", "flat", "Icon", IconCode.uiCrossSmall, bemClass.element("closeBtn")] });
        this._element.appendChild(closeButton);

        this._eventsController.addListener(closeButton, "click", (_event: MouseEvent) => {
            this.close();
        });
    }

    public dispose(): void {
        this._element.remove();
        this._eventsController.dispose();
    }
}

type PopupMenuOptionsCore = {
    /**
     * Nastaveni sirky
     * auto = automaticky podle sirky nadrazeneho elementu
     * content = automaticky podle sirky obsahu
     * number = pevne nastavena sirka
     */
    width: "auto" | "content" | number;
    /**
     * Nastaveni vysky
     * content = automaticky podle vysky obsahu
     * number = pevne nastavena vyska
     */
    height: "content" | number;
    /**
     * Obsah popup menu
     */
    content: HTMLElement;
    /**
     * Pokud je true, bude zobrazen hned po vytvoreni, aniz by bylo treba volat show()
     */
    showAfterCreate: boolean;
    /** Zobrazení křížku pro zavření */
    closeButton: boolean;
    /**
     * Whether the popup is shown when the target is focused. In this case, it is necessary
     * to specifically handle passing the focus back to the target when closing.
     */
    targetShowsPopupOnFocus?: boolean;
};

type PopupMenuOptionsElement = {
    /** Cilovy element na zaklade ktereho má byt zobrazen*/
    target: HTMLElement;
    /**
     * Pozice pro zobrazeni popup menu u elemenetu
     * auto = automaticky podle velikosti okna aplikace
     * after = vzdy pod elementem
     * before = vzdy nad elementem
     * overlay = překrytí elementu
     */
    showPosition: "auto" | "after" | "before" | "overlay";

} & PopupMenuOptionsCore;

type PopupMenuOptionsPosition = {
    /** Souradnice na kterych se popup menu zobrazi */
    target: { x: number; y: number };
} & PopupMenuOptionsCore;

export type PopupMenuOptions = PopupMenuOptionsElement | PopupMenuOptionsPosition;
